import React, { useState, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import axios from "axios";
import Bread from "../../common/Bread";
import { useForm } from "react-hook-form";
import { GoLocation } from "react-icons/go";
import { BsTelephone } from "react-icons/bs";
import { MdOutlineEmail } from "react-icons/md";
import "./contact.scss";
import ContactMap from "../../components/ContactMap";
import { toast } from "react-toastify";
import { ClipLoader } from "react-spinners";

const ContactUs = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

  const [formLoading, setFormLoading] = useState(false);
  const captchaRef = useRef(null);

  const breadcrumList = {
    image: "",
    title: "Contact Us",
    l1: "Home",
    l2: "Contact Us",
    l3: "",
  };

  const onSubmit = async (values) => {
    setFormLoading(true);

    // Get reCAPTCHA token
    const token = captchaRef.current.getValue();

    if (!token) {
      toast.error("You must confirm you are not a robot");
      setFormLoading(false);
      return;
    }

    try {
      const { data } = await axios.post(
        "https://admin.icfcbank.com/api/contacts/add_feedback",
        {
          ...values,
          token,
        }
      );

      if (data.status === "SUCCESS") {
        toast.success("Your message was successfully sent.");
        reset();
      } else {
        toast.error(data.status_message || "Something went wrong.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Submission failed. Please try again.");
    } finally {
      setFormLoading(false);
      captchaRef.current.reset(); // Reset captcha every time
    }
  };

  return (
    <Bread data={breadcrumList}>
      <div className="contactPageMainContainer">
        <div className="top-flex-container">
          <div className="flex-item">
            <div className="iconContainer">
              <GoLocation className="icon" aria-hidden="true" focusable="false" />
            </div>
            <div className="contentContainer">
              <h3>Address</h3>
              <p>
                ICFC Finance Limited - Head Office, Thirbam Sadhak-2,
                Bhatbhateni
              </p>
            </div>
          </div>

          <div className="flex-item">
            <div className="iconContainer">
              <BsTelephone className="icon" aria-hidden="true" focusable="false" />
            </div>
            <div className="contentContainer">
              <h3>Phone</h3>
              <p>
                <a href="tel:+97714525292" aria-label="Call 01-4525292">
                  01-4525292
                </a>
              </p>
            </div>
          </div>

          <div className="flex-item">
            <div className="iconContainer">
              <MdOutlineEmail className="icon" aria-hidden="true" focusable="false" />
            </div>
            <div className="contentContainer">
              <h3>Email</h3>
              <p>
                <a href="mailto:info@icfcbank.com">info@icfcbank.com</a>
              </p>
            </div>
          </div>
        </div>

        <div className="bottom-flex-container">
          <div className="formContainer">
            <h2>Contact Us</h2>
            <form className="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="contactFormInputContainer">
                <label htmlFor="contact-fullname">Full Name</label>
                <input
                  id="contact-fullname"
                  type="text"
                  autoComplete="name"
                  aria-required="true"
                  aria-invalid={errors.fullname ? "true" : "false"}
                  aria-describedby={errors.fullname ? "contact-fullname-err" : undefined}
                  {...register("fullname", {
                    required: "Full Name is Required",
                  })}
                  placeholder="Full Name"
                />
                {errors.fullname && (
                  <span id="contact-fullname-err" role="alert">
                    {errors.fullname.message}
                  </span>
                )}
              </div>

              <div className="contactFormInputContainer">
                <label htmlFor="contact-email">Email</label>
                <input
                  id="contact-email"
                  type="email"
                  autoComplete="email"
                  aria-required="true"
                  aria-invalid={errors.email ? "true" : "false"}
                  aria-describedby={errors.email ? "contact-email-err" : undefined}
                  {...register("email", {
                    required: "Email is Required",
                    pattern: {
                      value: /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/i,
                      message: "Invalid email address",
                    },
                  })}
                  placeholder="Email"
                />
                {errors.email && (
                  <span id="contact-email-err" role="alert">
                    {errors.email.message}
                  </span>
                )}
              </div>

              <div className="contactFormInputContainer">
                <label htmlFor="contact-phone">Mobile Number</label>
                <input
                  id="contact-phone"
                  type="tel"
                  autoComplete="tel"
                  aria-required="true"
                  aria-invalid={errors.phone ? "true" : "false"}
                  aria-describedby={errors.phone ? "contact-phone-err" : undefined}
                  {...register("phone", {
                    required: "Phone is Required",
                    pattern: {
                      value: /^[\+]?[0-9\s\-().]{7,20}$/,
                      message: "Invalid phone number",
                    },
                  })}
                  placeholder="Mobile Number"
                />
                {errors.phone && (
                  <span id="contact-phone-err" role="alert">
                    {errors.phone.message}
                  </span>
                )}
              </div>

              <div className="contactFormInputContainer">
                <label htmlFor="contact-address">Address</label>
                <input
                  id="contact-address"
                  type="text"
                  autoComplete="street-address"
                  aria-required="true"
                  aria-invalid={errors.address ? "true" : "false"}
                  aria-describedby={errors.address ? "contact-address-err" : undefined}
                  {...register("address", { required: "Address is Required" })}
                  placeholder="Address"
                />
                {errors.address && (
                  <span id="contact-address-err" role="alert">
                    {errors.address.message}
                  </span>
                )}
              </div>

              <div className="contactFormInputContainer">
                <label htmlFor="contact-message">Your Message</label>
                <textarea
                  id="contact-message"
                  rows="5"
                  aria-required="true"
                  aria-invalid={errors.message ? "true" : "false"}
                  aria-describedby={errors.message ? "contact-message-err" : undefined}
                  {...register("message", {
                    required: "Message is Required",
                    minLength: { value: 5, message: "Minimum 5 characters" },
                    maxLength: { value: 5000, message: "Max 5000 characters" },
                  })}
                  placeholder="Message"
                />
                {errors.message && (
                  <span id="contact-message-err" role="alert">
                    {errors.message.message}
                  </span>
                )}
              </div>

              <div className="captchaContainer" aria-label="CAPTCHA verification">
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_SITE_KEY}
                  ref={captchaRef}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || formLoading}
                aria-busy={formLoading}
                className="contactFormResetButton contactSubmitButton"
              >
                {formLoading && (
                  <ClipLoader size={20} color="#fff" aria-hidden="true" focusable="false" />
                )}
                Submit
              </button>
            </form>
          </div>

          <div className="mapContainer">
            <ContactMap />
          </div>
        </div>
      </div>
    </Bread>
  );
};

export default ContactUs;
